import { useEffect, createContext, useContext, ReactNode } from "react";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  // The app is LIGHT-ONLY for a consistent look — the admin never inherited a
  // toggle, so following the OS `prefers-color-scheme` was the only reason it ever
  // rendered dark. We keep the context API (useTheme consumers still work) but the
  // theme is pinned to "light" and the toggle/setTheme are no-ops.
  const theme: Theme = "light";

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("dark");
    root.classList.add("light");
    // Clear any previously-stored "dark" so a returning user isn't stuck dark.
    try {
      localStorage.setItem("theme", "light");
    } catch {
      /* localStorage unavailable — the class above is what matters */
    }
  }, []);

  const noop = () => {};

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme: noop, setTheme: noop }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
